<script setup>
import { computed, ref } from 'vue'
import { kg, formatKg, packagingTotals, positiveInteger, nonNegativeInteger } from '../../services/process/ProcessMetrics'
const props = defineProps({ items: { type: Array, default: () => [] }, inputs: Boolean, editable: Boolean })
const emit = defineEmits(['update:items'])
const grouping = ref('detail')
const headers = computed(() => [
  ...(props.inputs ? [{ title: 'Origen', key: 'sourceLabel' }, { title: 'Procedencia', key: 'originLabel' }] : []),
  { title: 'Ubicación', key: 'locationInPlant' }, { title: 'Calidad', key: 'qualityType.qualityType' },
  { title: 'Envase', key: 'packagingType.packagingType' }, { title: 'Bultos', key: 'bagQuantity', align: 'end' },
  { title: 'Kilos', key: 'amount', align: 'end' }, { title: 'Gramaje', key: 'gramaje', align: 'end' },
  ...(props.inputs ? [{ title: 'Cosecha', key: 'harvesting' }] : []),
  ...(props.editable ? [{ title: 'Acciones', key: 'actions', sortable: false }] : [])
])
const options = computed(() => [
  { title: 'Detalle', value: 'detail' }, { title: 'Por calidad', value: 'quality' },
  { title: 'Por ubicación', value: 'location' }, ...(props.inputs ? [{ title: 'Por origen', value: 'source' }] : [])
])
const groups = computed(() => {
  if (grouping.value === 'detail') return [{ label: 'Detalle', items: props.items }]
  const groups = new Map()
  for (const item of props.items) {
    const label = grouping.value === 'quality' ? item.qualityType?.qualityType || 'Sin calidad'
      : grouping.value === 'source' ? item.sourceLabel || 'Sin referencia' : item.locationInPlant || 'Sin ubicación'
    if (!groups.has(label)) groups.set(label, [])
    groups.get(label).push(item)
  }
  return [...groups].map(([label, items]) => ({ label, items }))
})
const total = items => items.reduce((sum, item) => sum + kg(item.amount), 0)
function change (item, key, value) {
  emit('update:items', props.items.map(row => row.id === item.id ? { ...row, [key]: value === '' ? '' : Number(value) } : row))
}
function remove (item) { emit('update:items', props.items.filter(row => row.id !== item.id)) }
</script>

<template>
  <div>
    <v-select v-model="grouping" :items="options" label="Vista de mercadería" class="mt-3" hide-details />
    <div v-for="group in groups" :key="group.label" class="mt-3">
      <h3 v-if="grouping !== 'detail'" class="text-subtitle-1">{{ group.label }} · {{ formatKg(total(group.items)) }} kg</h3>
      <div class="table-wrapper">
        <v-data-table :headers="headers" :items="group.items" :items-per-page="5" :items-per-page-options="[5, 10, 25, -1]" items-per-page-text="Filas por página" item-value="id">
          <template #[`item.gramaje`]="{ item }">{{ item.gramaje == null ? 'No aplica' : item.gramaje }}</template>
          <template #[`item.amount`]="{ item }">
            <v-text-field v-if="inputs && editable" :model-value="item.amount" type="number" label="Kg a procesar" min="1" step="1" density="compact" class="quantity-field"
              :hint="`Disponibles: ${formatKg(item.availableAmount ?? item.amount)} kg`" persistent-hint
              :rules="[positiveInteger, v => Number(v) <= (item.availableAmount ?? item.amount) || 'Supera los kilos disponibles.']"
              @update:model-value="change(item, 'amount', $event)" />
            <span v-else>{{ formatKg(item.amount) }} kg</span>
          </template>
          <template #[`item.bagQuantity`]="{ item }">
            <v-text-field v-if="inputs && editable" :model-value="item.bagQuantity" type="number" label="Bultos a usar" min="0" step="1" density="compact" class="quantity-field"
              :rules="[nonNegativeInteger, v => Number(v) <= (item.availableBags ?? item.bagQuantity) || 'Supera los bultos disponibles.']"
              @update:model-value="change(item, 'bagQuantity', $event)" />
            <span v-else>{{ formatKg(item.bagQuantity) }}</span>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn v-if="inputs && editable" icon="close" variant="text" size="small" :aria-label="`Quitar ${item.sourceLabel || 'mercadería'}`" @click="remove(item)" />
            <slot v-else name="actions" :item="item" />
          </template>
          <template #[`body.append`]>
            <tr class="font-weight-bold"><td :colspan="headers.length">{{ grouping === 'detail' ? 'Total del parte' : 'Subtotal del grupo' }}: {{ formatKg(total(group.items)) }} kg · {{ group.items.length }} registros</td></tr>
          </template>
          <template #no-data>No hay mercadería registrada.</template>
        </v-data-table>
      </div>
    </div>
    <div class="totals-bar pa-3 mt-3">
      <strong>Total del parte: {{ formatKg(total(items)) }} kg</strong>
      <span v-for="pack in packagingTotals(items)" :key="pack.label">{{ pack.label }}: {{ formatKg(pack.bags) }} bultos</span>
    </div>
  </div>
</template>

<style scoped>
.quantity-field { min-width: 160px; }
.totals-bar { display: flex; flex-wrap: wrap; gap: 12px; background: rgb(var(--v-theme-surface-variant)); }
</style>
